import { fiscalYearStart, fiscalYearLabel, isPartialFiscalYear } from '~/util/fiscalYear'
import { parseDateLocal } from '~/util/date'
import { GRANULARITY } from './chrome/chartChrome'

/** T5/T6: FY-aware x-axis tick formatter for points folded at unit 'fy', built against ONE
 *  [windowFrom, windowTo] pair. A bucket whose fiscal year the window does not fully cover holds
 *  a PART year while carrying a full-FY label, so it gets the partial marker.
 *
 *  Deliberately a BUILDER, not a single formatter: panels rendered side by side do not share a
 *  window (an in-window panel, the previous-period baseline segment, and a per-timeframe arm each
 *  have their own bounds), and checking a bucket against the wrong window silently drops or adds
 *  the marker. Callers build one per window — see ChartCanvas (in-window vs baseline) and
 *  DynamicsPanelsTile (per panel).
 *
 *  Extracted verbatim from ChartCanvas (T5) in T6 so the Dynamics rows reuse it rather than
 *  reimplementing fiscal arithmetic. */
export function buildFyTickFormatter(windowFromIso: string, windowToIso: string) {
  return (v: string | number): string => {
    if (v === 0 || v === 'auto') return ''
    const bucketStartIso = String(v)
    // Snap before labelling: fiscalYearLabel reads its argument AS the bucket start, while
    // isPartialFiscalYear snaps internally. Without this the two could name different fiscal
    // years for the same tick (e.g. 2024-02-01 → label 'FY 24/25', predicate about FY 23/24).
    // Unreachable today — tick values are always summariseByUnit 'fy' keys, i.e. FY starts — so
    // this makes the agreement structural rather than a property of the caller.
    const label = fiscalYearLabel(fiscalYearStart(parseDateLocal(bucketStartIso)))
    return isPartialFiscalYear(bucketStartIso, windowFromIso, windowToIso)
      ? `${label}${GRANULARITY.partialFyMarker}`
      : label
  }
}
